import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowGroupingModel,
  GridRowModes,
  GridRowModesModel,
  GridToolbar,
} from '@mui/x-data-grid-premium';
import { useState } from 'react';

// Sample data for testing
const rows = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john@example.com',
    department: 'Engineering',
    role: 'Senior Developer',
    salary: 95000,
    startDate: new Date('2020-01-15'),
    status: 'Active',
    performance: 4.5,
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane@example.com',
    department: 'Engineering',
    role: 'Tech Lead',
    salary: 120000,
    startDate: new Date('2019-03-20'),
    status: 'Active',
    performance: 4.8,
  },
  {
    id: 3,
    name: 'Bob Johnson',
    email: 'bob@example.com',
    department: 'Marketing',
    role: 'Marketing Manager',
    salary: 75000,
    startDate: new Date('2021-06-10'),
    status: 'Active',
    performance: 4.2,
  },
  {
    id: 4,
    name: 'Alice Brown',
    email: 'alice@example.com',
    department: 'Engineering',
    role: 'Junior Developer',
    salary: 65000,
    startDate: new Date('2022-09-01'),
    status: 'Active',
    performance: 3.9,
  },
  {
    id: 5,
    name: 'Charlie Wilson',
    email: 'charlie@example.com',
    department: 'Sales',
    role: 'Sales Director',
    salary: 110000,
    startDate: new Date('2018-11-15'),
    status: 'Active',
    performance: 4.6,
  },
];

export function MuiLicenseTest() {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rowGroupingModel, setRowGroupingModel] =
    useState<GridRowGroupingModel>(['department']);

  // MUI X automatically validates the license when components render
  // If there are license issues, watermarks and console warnings will appear automatically

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      editable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      editable: true,
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 130,
      editable: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      editable: true,
    },
    {
      field: 'salary',
      headerName: 'Salary',
      type: 'number',
      width: 120,
      editable: true,
      valueFormatter: (value) => `$${value}`,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      type: 'date',
      width: 120,
      editable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      editable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'Active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'performance',
      headerName: 'Performance',
      type: 'number',
      width: 120,
      editable: true,
      valueFormatter: (value) => `${value}/5.0`,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<span>💾</span>}
              label="Save"
              onClick={() => handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<span>❌</span>}
              label="Cancel"
              onClick={() => handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<span>✏️</span>}
            label="Edit"
            onClick={() => handleEditClick(id)}
          />,
        ];
      },
    },
  ];

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: any) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: any) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: any) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        MUI X Premium License Test
      </Typography>

      <Stack spacing={3}>
        {/* License Information */}
        <Alert severity="info" variant="outlined">
          <AlertTitle>MUI X Premium License Test</AlertTitle>
          MUI X automatically validates your license when components render. If
          your license is valid, you should see no watermarks and no console
          warnings. If there are license issues, watermarks and console warnings
          will appear automatically.
        </Alert>

        {/* Premium Features Test */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Testing Premium Features:
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip label="Row Grouping" color="primary" />
            <Chip label="Column Resizing" color="primary" />
            <Chip label="Column Pinning" color="primary" />
            <Chip label="Multi-Sorting" color="primary" />
            <Chip label="Multi-Filtering" color="primary" />
            <Chip label="Excel Export" color="primary" />
            <Chip label="Row Editing" color="primary" />
            <Chip label="Advanced Toolbar" color="primary" />
          </Stack>
        </Paper>

        <Divider />

        {/* Data Grid with Premium Features */}
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGridPremium
            rows={rows}
            columns={columns}
            rowGroupingModel={rowGroupingModel}
            onRowGroupingModelChange={setRowGroupingModel}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={(newRow) => {
              console.log('Row updated:', newRow);
              return newRow;
            }}
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  // Hide some columns by default
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            pagination
            disableRowSelectionOnClick
            // Premium features
            disableColumnResize={false}
            disableColumnPinning={false}
            disableMultipleColumnsSorting={false}
            disableMultipleColumnsFiltering={false}
            // Enable row grouping
            groupingColDef={{
              headerName: 'Group',
              width: 200,
            }}
          />
        </Box>

        {/* Instructions */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            How to Test:
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>
                <strong>Check for watermarks:</strong> Look for any "MUI X"
                watermarks in the data grid area. If you see watermarks, there's
                a license issue.
              </li>
              <li>
                <strong>Check console warnings:</strong> Open browser dev tools
                and look for any license-related warnings. Valid licenses show
                no warnings.
              </li>
              <li>
                <strong>Test Premium features:</strong>
                <ul>
                  <li>
                    Try grouping by department (drag "Department" to the
                    grouping area)
                  </li>
                  <li>Resize columns by dragging column borders</li>
                  <li>Pin columns by right-clicking on column headers</li>
                  <li>
                    Use multi-sorting by holding Shift and clicking column
                    headers
                  </li>
                  <li>
                    Use the filter button in the toolbar for multi-column
                    filtering
                  </li>
                  <li>Try editing rows by clicking the edit button</li>
                  <li>Use the Excel export button in the toolbar</li>
                </ul>
              </li>
              <li>
                <strong>Success indicators:</strong> No watermarks, no console
                warnings, and all Premium features work without restrictions.
              </li>
            </ol>
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
}
